import { useState } from 'react';

type SetItemType = Storage['setItem'];

// https://usehooks.com/useLocalStorage/
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- TODO this is load-bearing, the build fails without it
export const useLocalStorage = <T, _>(
  key: Parameters<SetItemType>[0],
  initialValue: T,
  storage: Storage = window.localStorage,
): [T | undefined, (newValue: T) => void, () => void] => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    try {
      // Get from local storage by key
      const item = storage.getItem(key);
      // Parse stored json or if none return initialValue
      return item !== null && Boolean(item) ? (JSON.parse(item) as T | undefined) : initialValue;
    } catch (error: unknown) {
      // If error also return initialValue
      console.log(error); // eslint-disable-line no-console
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((currentValue: T | undefined) => T | undefined)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      storage.setItem(key, JSON.stringify(valueToStore));
    } catch (error: unknown) {
      // A more advanced implementation would handle the error case
      console.error(error); // eslint-disable-line no-console
    }
  };

  const removeValue = () => {
    storage.removeItem(key);
    setStoredValue(undefined);
  };

  return [storedValue, setValue, removeValue];
};
